


















































































































import {
  defineComponent,
  PropType,
  ref,
  computed
} from '@nuxtjs/composition-api';
import { useVSFContext } from '@vue-storefront/core';
import { useTranslations } from '@vue-storefront/novulo';
import { mediaUrlFallback, StatusReference } from '@/helpers/utils';
import type { ProductVariationType } from '@/components/atoms/ProductDropdown.vue';
import type { UspType } from '../molecules/BuySectionUsps.vue';
import useWindowResize from '@/hooks/useResizeWindow.js';
import usePromotionsDrawer from '~/hooks/usePromotionsDrawer';

export default defineComponent({
  name: 'BuySection',
  props: {
    productStatusReference: {
      type: Number as PropType<StatusReference>,
      default: StatusReference.IN_STOCK
    },
    product: {
      type: Object,
      default: () => ({
        name: '',
        status: 'Op voorraad',
        manufacturers_suggested_retail_price: 0,
        first_price: 0,
        price: 0,
        price_after_cash_discount: 0,
        price_after_cashback_discount: 0,
        variations: [],
        spli_guid: '',
        spli_id: 0,
        sku: ''
      })
    },
    usps: {
      type: Array as PropType<UspType[]>,
      required: true,
      default: () => []
    }
  },
  setup(props, { emit }) {
    const { $novulo } = useVSFContext();
    const { translate } = useTranslations();
    const { isDesktop } = useWindowResize();
    const { openPromotionsDrawer } = usePromotionsDrawer();

    const variationsOpen = ref(false);
    const formattedVariations: ProductVariationType[] = [
      {
        // eslint-disable-next-line camelcase
        name: props.product.name,
        secondHandCondition: props.product?.second_hand_condition,
        // eslint-disable-next-line camelcase
        price:
          props.product.price_after_cash_discount &&
          props.product.price_after_cash_discount < props.product.price
            ? props.product.price_after_cash_discount
            : props.product.price,
        cashbackPrice: props.product.price_after_cashback_discount,
        id: props.product.spli_id ? props.product.spli_id.toString() : '',
        media: props.product.product_image_gallery
          ? props.product.product_image_gallery?.[0]?.media_url
          : '',
        alt: props.product.product_image_gallery
          ? props.product.product_image_gallery?.[0]?.media_file_name
          : '',
        url: '',
        disabled: false
      },
      ...props.product.variations
        .filter((variation) => variation.name !== null)
        .map((variation) => ({
          ...variation,
          name: variation.name,
          secondHandCondition: variation?.second_hand_condition,
          // eslint-disable-next-line camelcase
          price:
            variation.price_after_cash_discount &&
            variation.price_after_cash_discount < variation.price
              ? variation.price_after_cash_discount
              : variation.price,
          alt: variation.media_file_name,
          cashbackPrice: variation.price_after_cashback_discount,
          id: variation.id?.toString() || ''
        }))
    ];
    const selectedVariation = ref(formattedVariations[0]);

    function mapToProductVariation(item: any): ProductVariationType {
      const id = (item.spli_id || item.id || '').toString();
      const price =
        item.price_after_cash_discount && item.price_after_cash_discount < item.price
          ? item.price_after_cash_discount
          : item.price;

      return {
        id,
        name: item.name,
        secondHandCondition: item?.second_hand_condition,
        price,
        cashbackPrice: item.price_after_cashback_discount,
        url: item.url || '',
        media:
          item.media ||
          item.product_image_gallery?.[0]?.media_url ||
          '',
        alt:
          item.media_file_name ||
          item.product_image_gallery?.[0]?.media_file_name ||
          '',
        disabled: !item.can_be_ordered
      };
    }

    const currentSecondHandItem = mapToProductVariation(props.product);

    const mappedSecondHandList = (props.product.second_hand_variations || []).map(mapToProductVariation);

    const formattedSecondHandVariations: ProductVariationType[] = [
      currentSecondHandItem,
      ...mappedSecondHandList.filter(v => v.id !== currentSecondHandItem.id)
    ];

    const handleVariationSelectClick = () => {
      variationsOpen.value = !variationsOpen.value;
    };

    const updateSelectedVariation = (id: string) => {
      selectedVariation.value = formattedVariations.find(v => v.id === id);
    };

    const selectedSecondHandVariation = ref(formattedSecondHandVariations[0]);

    const updateSelectedSecondHandVariation = (id: string) => {
      const next = formattedSecondHandVariations.find(v => v.id === id);
      selectedSecondHandVariation.value = next || selectedSecondHandVariation.value;
    };

    const isDelivery = computed(
      () =>
        (props.productStatusReference === StatusReference.IN_STOCK &&
          props.product.has_stores) ||
        props.productStatusReference === StatusReference.TEMPORARILY_SOLD_OUT
    );

    const isPreorder = computed(
      () => props.productStatusReference === StatusReference.PRE_ORDER
    );

    const shouldShowCarePlan = computed(() => {
      const wt = $novulo.config.state.getWt();
      // hide on PG and KE BE
      return ![7, 20, 42].includes(wt);
    });

    const isInsuranceSelected = ref(false);
    const handleAddToCart = (product) => {
      isInsuranceSelected.value = false;
      emit('addtocart', product);
    };

    return {
      variationsOpen,
      formattedVariations,
      selectedVariation,
      isDesktop,
      isDelivery,
      isPreorder,
      shouldShowCarePlan,
      handleVariationSelectClick,
      updateSelectedVariation,
      translate,
      openPromotionsDrawer,
      mediaUrlFallback,
      StatusReference,
      isInsuranceSelected,
      handleAddToCart,
      formattedSecondHandVariations,
      selectedSecondHandVariation,
      updateSelectedSecondHandVariation
    };
  }
});
